<template>
  <v-card min-width="600px" max-width="1000px" flat>
    <v-card-title class="d-flex align-center gap-2 pb-2">
      <v-icon>mdi-history</v-icon>
      <span>Mutation History</span>
    </v-card-title>
    <v-divider></v-divider>

    <div class="text-center">
      <v-progress-circular
        v-if="mutation_logs_count && !record.LogMutations"
        indeterminate
        class="py-10"
        color="orange"
      ></v-progress-circular>
    </div>

    <v-timeline
      v-if="record.LogMutations && record.LogMutations.length"
      density="compact"
      align="start"
      line-thickness="2"
      line-color="grey-lighten-2"
      class="pa-4"
    >
      <v-timeline-item
        v-for="(log, index) in record.LogMutations"
        :key="index"
        :dot-color="log.Status === 'ERROR' ? 'error' : 'success'"
        :icon="log.Status === 'ERROR' ? 'mdi-alert-circle' : 'mdi-check-circle'"
        fill-dot
      >
        <div class="timeline-content">
          <!-- Main Timeline Item -->
          <div class="d-flex justify-space-between align-start gap-2 mb-2">
            <div class="flex-grow-1">
              <div class="text-body2 font-weight-medium">
                {{ log.Action_Name }}
              </div>
              <div class="text-caption text-grey mt-1">
                {{ formatDate(log.Log_TS) }}
                <span v-if="log.Log_User" class="text-blue-darken-1">• {{ log.Log_User }}</span>
              </div>
            </div>
            <v-chip
              :color="log.Status === 'ERROR' ? 'error' : 'success'"
              size="x-small"
              label
              class="flex-shrink-0 text-white"
            >
              {{ log.Status }}
            </v-chip>
          </div>

          <!-- Toggle Details Button -->
          <v-btn
            v-if="log.Error_Message || log.Payload_JSON"
            size="x-small"
            variant="text"
            class="mt-2"
            @click="toggleDetails(index)"
          >
            <v-icon size="x-small" class="mr-1">
              {{ expandedDetails[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
            <span class="text-caption">{{ expandedDetails[index] ? 'Hide' : 'Show' }} Details</span>
          </v-btn>

          <!-- Details Content -->
          <div v-if="expandedDetails[index]" class="mt-3">
            <p v-if="log.Error_Message" class="text-error mb-3 break-word text-caption">
              <strong>⚠️ Error:</strong> {{ log.Error_Message }}
            </p>

            <div v-if="log.Payload_JSON" class="mt-3">
              <span class="text-caption font-weight-medium">Payload Details:</span>
              <div class="bg-grey-lighten-5 border rounded mt-2 pa-3">
                <vue-json-pretty
                  :data="parsePayload(log.Payload_JSON)"
                  :deep="2"
                  showLength
                  :showIcon="false"
                  :showLineNumber="false"
                />
              </div>
            </div>
          </div>
        </div>
      </v-timeline-item>
    </v-timeline>

    <v-card-text v-if="!record.LogMutations || !record.LogMutations.length" class="text-center text-grey py-6">
      <v-icon class="d-block mb-2">mdi-history</v-icon>
      <span>No mutation history available</span>
    </v-card-text>
  </v-card>
</template>

<script>
import GET_PRODUCT_MATCHING from '@/graphql/Product/getProductMatching.gql'
import GET_PRODUCT from '@/graphql/Product/getProduct.gql'
import GET_LINE from '@/graphql/Line/getLine.gql'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

export default {
  name: 'MutationLogs',
  components: {
    VueJsonPretty
  },
  data() {
    return {
      getProductMatching: {},
      getProduct: {},
      getLine: {},
      expandedDetails: {}
    }
  },
  props: {
    record_id: {
      type: Number,
      required: true
    },
    itemKey: {
      type: String,
      required: true
    },
    mutation_logs_count: {
      type: Number
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    parsePayload(jsonString) {
      try {
        return JSON.parse(jsonString);
      } catch {
        return { error: 'Invalid JSON', raw: jsonString };
      }
    },
    toggleDetails(index) {
      this.expandedDetails[index] = !this.expandedDetails[index];
      this.$forceUpdate();
    }
  },
  computed: {
    record() {
      if (this.itemKey === 'Product_Matching_SK') {
        return this.getProductMatching || {};
      } else if (this.itemKey === 'Product_SK') {
        return this.getProduct || {};
      } else if (this.itemKey === 'Line_SK') {
        return this.getLine || {};
      }
      return {};
    }
  },
  apollo: {
    getProductMatching: {
      query: GET_PRODUCT_MATCHING,
      variables() {
        return {
          Product_Matching_SK: this.record_id
        }
      },
      skip() {
        return this.itemKey !== 'Product_Matching_SK'
      }
    },
    getProduct: {
      query: GET_PRODUCT,
      variables() {
        return {
          Product_SK: this.record_id
        }
      },
      skip() {
        return this.itemKey !== 'Product_SK'
      }
    },
    getLine: {
      query: GET_LINE,
      variables() {
        return {
          Line_SK: this.record_id
        }
      },
      skip() {
        return this.itemKey !== 'Line_SK'
      }
    }
  }
}
</script>

<style scoped>
.break-word {
  word-break: break-word;
  white-space: pre-wrap;
}

.timeline-content {
  padding-right: 12px;
}

:deep(.vjs-tree) {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  max-height: 350px;
  overflow-y: auto;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
